import {SIZE_RECRUITMENT_MARKER, SIZE_RECRUITMENT_TEXTURE_KEYS} from '../configs/sizeRecruitmentMarker';
import {
  SIZE_RECRUITMENT_MARKER_PRICE_TEXT_STYLE,
  SIZE_RECRUITMENT_MARKER_SIZE_TEXT_STYLE,
} from '../styles/HexGrid.style';
import {ensureSizeRecruitmentTextures} from '../utils/effects/sizeRecruitmentTextures';

// Над жетонами, но под отрядами соседних ячеек: ореол выходит за свою ячейку
const HALO_DEPTH = 2.5;
const PLATE_DEPTH = 5;
const CONTENT_DEPTH = 6;

/**
 * Маркер докупки размера на квартале (см. configs/sizeRecruitmentMarker).
 * Элементы маркера принадлежат ячейке: их убирает HexGridRenderer при перерисовке
 *
 * @param {Phaser.Scene} scene
 * @param {Object} params
 * @param {number} params.x — центр медальона
 * @param {number} params.y
 * @param {number} params.hexSize
 * @param {number} params.price — цена докупки
 * @param {number} params.sizeToAdd — сколько размера прибавит (0 — уже максимальный)
 * @param {number} [params.linearFilter] — режим фильтрации LINEAR для текстур
 * @returns {{elements: Object[], halo: Object|null}} все элементы и ореол, который
 *   пульсирует (у потухшего маркера его нет)
 */
export const createUnitSizeRecruitmentMarker = (scene, {x, y, hexSize, price, sizeToAdd, linearFilter}) => {
  ensureSizeRecruitmentTextures(scene, {linearFilter});
  const {plateSizeFactor, halo: haloConfig, sizeRow, priceRow, disabled} = SIZE_RECRUITMENT_MARKER;
  const isDisabled = sizeToAdd <= 0;

  const addSizedImage = (imageX, imageY, textureKey, sizePx, depth) => {
    const image = scene.add.image(imageX, imageY, textureKey);
    image.setScale(sizePx / (image.width || 1));
    image.setDepth(depth);
    return image;
  };

  let halo = null;
  if (!isDisabled) {
    halo = addSizedImage(x, y, SIZE_RECRUITMENT_TEXTURE_KEYS.HALO, hexSize * haloConfig.sizeFactor, HALO_DEPTH);
    halo.setTint(haloConfig.tint);
    halo.baseScale = halo.scaleX;
  }

  const plate = addSizedImage(x, y, SIZE_RECRUITMENT_TEXTURE_KEYS.PLATE, hexSize * plateSizeFactor, PLATE_DEPTH);

  const sizeRowY = y + hexSize * sizeRow.y;
  const sizeText = scene.add.text(
    x + hexSize * sizeRow.textX, sizeRowY, `+${sizeToAdd}`, SIZE_RECRUITMENT_MARKER_SIZE_TEXT_STYLE,
  );
  sizeText.setScale(sizeRow.textScale);
  sizeText.setOrigin(0.5);
  sizeText.setDepth(CONTENT_DEPTH);
  sizeText.setTint(sizeRow.textTintTop, sizeRow.textTintTop, sizeRow.textTintBottom, sizeRow.textTintBottom);

  const sizeIcon = addSizedImage(
    x + hexSize * sizeRow.iconX, sizeRowY, SIZE_RECRUITMENT_TEXTURE_KEYS.PEOPLE, hexSize * sizeRow.iconSizeFactor, CONTENT_DEPTH,
  );
  sizeIcon.setTint(sizeRow.iconTint);

  const priceRowY = y + hexSize * priceRow.y;
  const priceText = scene.add.text(
    x + hexSize * priceRow.textX, priceRowY, `${price}`, SIZE_RECRUITMENT_MARKER_PRICE_TEXT_STYLE,
  );
  priceText.setScale(priceRow.textScale);
  priceText.setOrigin(0.5);
  priceText.setDepth(CONTENT_DEPTH);

  const coinsIcon = scene.add.image(x + hexSize * priceRow.iconX, priceRowY, 'coins_icon');
  coinsIcon.setScale(priceRow.iconScale);
  coinsIcon.setDepth(CONTENT_DEPTH);

  const content = [sizeText, sizeIcon, priceText, coinsIcon];
  if (isDisabled) {
    plate.setTint(disabled.plateTint);
    content.forEach((element) => element.setTint(disabled.contentTint));
    [plate, ...content].forEach((element) => element.setAlpha(disabled.alpha));
  }

  return {elements: [...(halo ? [halo] : []), plate, ...content], halo};
};

/**
 * Пульс ореолов: один на все маркеры. Поле перерисовывается на каждое обновление
 * стора и пересоздает маркеры, а пульс живет отдельно и переживает перерисовки —
 * иначе свечение начиналось бы заново от каждого действия. Заодно все маркеры
 * дышат в такт.
 */
export class UnitSizeRecruitmentMarkerPulse {
  /**
   * @param {Phaser.Scene} scene
   * @param {Object} [config] — такт и размах пульса (плашки дохода дышат тише)
   */
  constructor(scene, config = SIZE_RECRUITMENT_MARKER.halo) {
    this.scene = scene;
    this.config = config;
    this.halos = [];
    this.driver = {share: 0};
    this.tween = null;
  }

  /** Какие ореолы сейчас на поле. Пусто — пульс останавливается */
  setHalos(halos) {
    this.halos = halos;
    if (!halos.length) {
      this.stop();
      return;
    }
    if (!this.tween && this.scene?.tweens) {
      this.tween = this.scene.tweens.add({
        targets: this.driver,
        share: 1,
        duration: this.config.pulseMs,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
        onUpdate: () => this.apply(),
      });
    }
    this.apply();
  }

  apply() {
    const {minAlpha, maxAlpha, minScale, maxScale} = this.config;
    const {share} = this.driver;
    this.halos.forEach((halo) => {
      halo.setAlpha(minAlpha + (maxAlpha - minAlpha) * share);
      halo.setScale(halo.baseScale * (minScale + (maxScale - minScale) * share));
    });
  }

  stop() {
    this.tween?.stop?.();
    this.tween = null;
    this.driver.share = 0;
  }
}
